import { getConfig } from '@/utils/config/config';
import { COOKIES } from '@/utils/cookies';
import { buildApiUrl, buildUrl } from '@/utils/url/build-url';
import { type Brand } from '@/types/constants';
import { getCookieValue } from '@/utils/cookies';
import { fetchApi } from './fetcher';

const UNAUTHORIZED_CODE = 'UAG-005';

const getHeaders = async () => {
    const region = await getCookieValue(COOKIES.REGION);
    const brand = process.env.BRAND as Brand;

    return {
        'x-region-code': region,
        'x-brand': brand,
        ...{ accept: 'application/json' }
    };
};

const getFavoriteGamesClientBaseUrl = () => {
    const config = getConfig();
    return buildApiUrl(config.apis.FavoriteGamesClient);
};

const callFavoriteGamesApi = async (
    action: 'add' | 'delete',
    gameCode: string,
    platform: string = 'desktop'
): Promise<boolean> => {
    const baseUrl = getFavoriteGamesClientBaseUrl();
    const headers = await getHeaders();

    const url = buildUrl(`${baseUrl}/${action}`, {
        gameCode,
        product: process.env.PRODUCT as string,
        gameIdentifierType: 'uid',
        platform
    });

    try {
        const networkConfig = getConfig().network?.InternalFavoriteGamesClient;
        const res = await fetchApi(
            url,
            { method: 'GET', headers, credentials: 'include' },
            networkConfig ? { networkConfig } : undefined
        );
        const text = await res.text();

        if (!text) {
            return false;
        }

        let json;
        try {
            json = JSON.parse(text);
        } catch {
            console.warn(`[${action}FavoriteGame] Response not JSON:`, text);
            return false;
        }

        if (Array.isArray(json) && json.length === 0) {
            return false;
        }
        if (json.code === UNAUTHORIZED_CODE) {
            console.warn(`[${action}FavoriteGame] Unauthorized:`, json.details?.message);
            return false;
        }

        return res.ok;
    } catch (e) {
        console.error(`${action}FavoriteGame error: ${(e as Error).message}`);
        return false;
    }
};

export const apiAddFavoriteGame = (gameCode: string, platform?: string) =>
    callFavoriteGamesApi('add', gameCode, platform);

export const apiRemoveFavoriteGame = (gameCode: string, platform?: string) =>
    callFavoriteGamesApi('delete', gameCode, platform);
